import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { useCustomSignOffEnabled } from 'hooks/models/organization/useCustomSignOffEnabled';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import {
  GetAllOrganizationSignOffQuestionsQuery,
  GetAllOrganizationSignOffQuestionsQueryVariables,
  OrganizationSignOffQuestionFilter,
  SortOrder,
} from '__generated__/graphql';

export type OrganizationSignOffQuestionItem = Omit<
  NonNullable<GetAllOrganizationSignOffQuestionsQuery['organizationSignOffQuestions']>[number],
  'cursor'
>;

const EMPTY_QUESTIONS: OrganizationSignOffQuestionItem[] = [];

export default function useGetOrganizationSignOffQuestions(filter?: OrganizationSignOffQuestionFilter) {
  const pageAll = useGraphQLPaging();
  const baseQueryKey = useReactQueryBaseKey();
  const isCustomSignOffEnabled = useCustomSignOffEnabled();
  const query = useQuery({
    enabled: isCustomSignOffEnabled,
    queryKey: [...baseQueryKey, 'getAllOrganizationSignOffQuestions', filter],
    queryFn: (): Promise<OrganizationSignOffQuestionItem[]> => {
      return pageAll<GetAllOrganizationSignOffQuestionsQuery, GetAllOrganizationSignOffQuestionsQueryVariables>(
        {
          document: QUERY_ORGANIZATION_SIGN_OFF_QUESTIONS,
          variables: {
            filter: { ...filter },
            sort: [{ order: SortOrder.Asc }],
          },
        },
        'organizationSignOffQuestions'
      );
    },
  });

  return { ...query, data: query.data ?? EMPTY_QUESTIONS };
}

export const QUERY_ORGANIZATION_SIGN_OFF_QUESTIONS = gql`
  query getAllOrganizationSignOffQuestions(
    $filter: OrganizationSignOffQuestionFilter
    $first: Int
    $sort: [OrganizationSignOffQuestionSort!]
    $after: String
  ) {
    organizationSignOffQuestions(filter: $filter, first: $first, sort: $sort, after: $after) {
      id
      audienceType
      choices
      createdOn
      cursor
      deletedOn
      organizationId
      positionId
      position {
        id
        title
      }
      questionDescription
      questionPrompt
      questionTitle
      submittedOn
      type
      updatedOn
      order
    }
  }
`;
